import React, { memo, useMemo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { Alert, Row } from "reactstrap";
import {
  AttachmentInteractionInit,
  resetAttachmentsInteraction,
  getAttachmentsList,
} from "../../../store/attachmentsInteraction/actions";
import Button from "../Button";

import "./style.scss";
import Accordions from "./subcomponents/Accordions";
import AttachmentUploadModal from "./subcomponents/AttachmentUploadModal";

function AttachmentsInteractionComponent({ activeChat, isSpy = false }) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!activeChat) return;
    dispatch(AttachmentInteractionInit(activeChat));
  }, [dispatch, activeChat]);

  useEffect(() => {
    return () => {
      if (isSpy) dispatch(resetAttachmentsInteraction(activeChat));
    };
  }, [dispatch, activeChat, isSpy]);

  if (!activeChat) return null;

  return (
    <div className="attachments-interaction">     
      <AnchorInteractionArea activeChat={activeChat} />
      <div className="content-attachments">
        <StatusInform activeChat={activeChat} />
        <Accordions activeChat={activeChat} />
      </div>
    </div>
  );
}

export default memo(AttachmentsInteractionComponent);

const StatusInform = memo(({ activeChat }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    status: { getAttachmentsList: status } = {},
    notificationTab = false,
  } = useSelector((state) => state.AttachmentsInteraction?.[activeChat]) || {};

  useEffect(() => {
    if (!activeChat) return;
    if (!status) return;
    if (status?.success || status?.loading) return;

    if (status?.error) {
      const timeout = setTimeout(() => {
        dispatch(getAttachmentsList(activeChat));
      }, 5000);

      return () => clearTimeout(timeout);
    }
    dispatch(getAttachmentsList(activeChat));
  }, [dispatch, activeChat, status]);

  return (
    <>
      {notificationTab && (
        <Alert color="custom">
          <i className="bx bx-info-circle mr-2" />
          {t("Remember to tab pending so the customer is notified correctly")}
        </Alert>
      )}
      {status?.error && (
        <div
          title={t(
            "Sorry! something went wrong, please contact our support team"
          )}
        >
          <i className="fa fa-exclamation-triangle text-danger mr-2" />
        </div>
      )}

      {status?.loading && (
        <div>
          <i className="bx bx-loader bx-spin text-primary mr-2" />
        </div>
      )}
    </>
  );
});

const AnchorInteractionArea = memo(({ activeChat }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isOperatorArea = history.location.pathname === "/chats";
  const [isAttachmentUploadModalOpen, setAttachmentUploadModalOpen] =
    useState(false);

  const redirect = useMemo(() => {
    if (isOperatorArea) {
      return {
        pathname: "/attachments-interaction",
        state: { activeChat },
      };
    }

    return {
      pathname: "/chats",
      state: { chatId: activeChat },
    };
  }, [activeChat, isOperatorArea]);

  return (
    <>
      <Row
        className="d-flex w-100"
        style={
          isOperatorArea
            ? { flexWrap: "nowrap" }
            : { flexWrap: "nowrap", flexDirection: "row-reverse" }
        }
      >
        <AttachmentUploadModal
          isAttachmentUploadModalOpen={isAttachmentUploadModalOpen}
          setAttachmentUploadModalOpen={setAttachmentUploadModalOpen}
        />
        <Button
          variant={isOperatorArea ? "outlined" : "contained"}
          onClick={() =>
            setAttachmentUploadModalOpen(!isAttachmentUploadModalOpen)
          }
        >
          {t("add")}
          <i className="bx bx-plus mr-1" />
        </Button>
        <Link to={redirect} className="d-flex w-100">
          <Button variant={isOperatorArea ? "contained" : "outlined"}>
            {isOperatorArea ? (
              <>
                {t("analyze")}
                <i className="bx bx-check mr-1" />
              </>
            ) : (
              <>
                <i className="bx bx-arrow-back mr-1" />
                {t("back")}
              </>
            )}
          </Button>
        </Link>
      </Row>
    </>
  );
});
